import {
  COST_BASE_API_URL,
  ITEMS_BASE_API_URL,
  STORES_BASE_API_URL,
  SUPPLIERS_BASE_API_URL,
} from "src/utils/constants";

/**
 * Background API warm-up.
 *
 * Each API declares a HealthCheck operation that reports on the service *and*
 * its database, so calling it opens a DB connection and primes the Oracle pool
 * and EF model. Without that, the first real request after an app pool goes
 * idle pays the whole cold start, which users experience as a slow first screen.
 *
 * These specific endpoints are used because they are the only health-style
 * operations actually published through APIM. The apps also expose an anonymous
 * root `/warmup`, which would be preferable (it needs no token, so it could
 * overlap the MSAL sign-in round-trip) — but it is not declared in any
 * api-spec.json, so the gateway rejects it before it reaches the app. Switching
 * to HealthCheck trades that overlap for working without an APIM change.
 *
 * MUST NEVER COMPETE WITH THE PAGE IT IS WARMING. Being fire-and-forget is not
 * sufficient on its own: every RP API sits behind the SAME APIM host, so firing
 * four requests at once claims four of that host's connection slots, and each
 * one deliberately touches the database — precisely the resource a cold pool is
 * slowest to hand over. Issued during initial render they can queue ahead of the
 * requests the screen actually needs, which reads to the user as the page
 * hanging on load. So the warm-up:
 *
 *   * waits for the browser to be idle before starting at all, and
 *   * goes one API at a time with a gap between them, never in parallel, and
 *   * gives up quickly, because a warm-up still waiting after a few seconds has
 *     already failed at its purpose and is now pure cost.
 *
 * Deliberately fire-and-forget beyond that. A warm-up is an optimisation, never
 * a dependency: every failure is swallowed, nothing is awaited by the UI, and no
 * error reaches the user. It uses plain `fetch` rather than the shared axios
 * instance on purpose — axios carries auth-refresh interceptors and error
 * reporting that would treat a warm-up blip as a real API failure.
 */

/** How long a warm-up is considered good for before it's worth repeating. */
const WARM_TTL_MS = 5 * 60 * 1000;

/**
 * Give up on a single warm-up quickly. Intentionally far shorter than a real
 * request's budget: the point is to prime a pool, and holding a connection to
 * the shared APIM host for longer costs the page more than the warm-up can save.
 */
const WARM_TIMEOUT_MS = 4_000;

/** Longest we'll wait for an idle moment before warming anyway. */
const IDLE_TIMEOUT_MS = 3_000;

/** Fallback delay when requestIdleCallback isn't available (e.g. Safari, jsdom). */
const FALLBACK_DELAY_MS = 1_500;

/** Gap between APIs, so only one warm-up connection is ever open at a time. */
let staggerMs = 400;

/**
 * Each API's published health operation. The path differs per API, so they are
 * paired with their base URL rather than assuming a shared suffix.
 */
const HEALTH_PATHS: ReadonlyArray<readonly [string, string]> = [
  [ITEMS_BASE_API_URL, "/api/item/HealthCheck"],
  [STORES_BASE_API_URL, "/api/store/HealthCheck"],
  [SUPPLIERS_BASE_API_URL, "/api/supplier/HealthCheck"],
  [COST_BASE_API_URL, "/api/cost/HealthCheck"],
];

let lastWarmedAt = 0;
let inFlight = false;

/** Distinct health-check URLs. Deduped: some environments collapse the hosts. */
function warmupTargets(): string[] {
  const seen = new Set<string>();
  for (const [base, path] of HEALTH_PATHS) {
    if (!base) continue;
    // Trim any trailing slash so we don't produce "…/V1//api/…".
    seen.add(`${base.replace(/\/+$/, "")}${path}`);
  }
  return [...seen];
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Run once the browser has finished the work the user is actually waiting on. */
function whenIdle(run: () => void): void {
  const ric = (
    globalThis as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }
  ).requestIdleCallback;

  if (typeof ric === "function") {
    // The timeout matters: on a busy page idle may never arrive on its own, and
    // a warm-up that never runs is just dead code.
    ric(run, { timeout: IDLE_TIMEOUT_MS });
    return;
  }
  setTimeout(run, FALLBACK_DELAY_MS);
}

/** One health check. Never throws. */
async function pingOne(url: string, accessToken: string): Promise<void> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), WARM_TIMEOUT_MS);
  try {
    await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${accessToken}` },
      signal: controller.signal,
      credentials: "omit",
      cache: "no-store",
    });
  } catch {
    // Swallowed on purpose — see the file comment.
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Ping every API's health check. Throttled, so calling it on each page load is
 * safe — repeat calls inside the TTL are no-ops.
 *
 * Returns immediately: the work is deferred to an idle callback and then run one
 * API at a time, so it cannot delay the render or the screen's own requests.
 *
 * @param accessToken bearer token; the operations inherit the gateway's global
 *   Bearer security, so without one every call would 401 and warm nothing.
 *   Calling before a token exists is a deliberate no-op rather than an error.
 * @param force skip the throttle
 */
export function warmApis(accessToken: string, force = false): void {
  // jsdom-based unit tests have no fetch; nothing to warm there either.
  if (typeof fetch !== "function") return;
  if (!accessToken) return;
  if (inFlight) return;
  if (!force && Date.now() - lastWarmedAt < WARM_TTL_MS) return;

  // Claim the slot now rather than inside the idle callback, so a burst of
  // callers (a token refresh and a tab focus landing together) can't queue
  // several passes behind one another.
  inFlight = true;
  lastWarmedAt = Date.now();

  whenIdle(() => {
    void (async () => {
      try {
        const targets = warmupTargets();
        for (let i = 0; i < targets.length; i++) {
          await pingOne(targets[i], accessToken);
          // Gap between APIs, but not after the last one.
          if (staggerMs > 0 && i < targets.length - 1) await sleep(staggerMs);
        }
      } finally {
        inFlight = false;
      }
    })();
  });
}

/**
 * Test seam: forget that a warm-up happened, and optionally collapse the stagger
 * so specs don't have to wait out real delays.
 */
export function resetWarmupThrottleForTests(opts?: { staggerMs?: number }): void {
  lastWarmedAt = 0;
  inFlight = false;
  if (opts?.staggerMs !== undefined) staggerMs = opts.staggerMs;
}
